import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Store, Star, MapPin, Crown, Search, Filter, Send, CheckCircle, 
  ExternalLink, Phone, Mail, Globe, Sparkles, Shield, Users, Lock, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { encryptCardData } from '../utils/encryption';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const directoryListings = [
  { id: 1, name: "Crystal Visions Healing Center", category: "Healing Center", location: "Los Angeles, CA", featured: true, rating: 4.9, reviews: 127, services: ["Crystal Healing", "Reiki", "Sound Therapy"] },
  { id: 2, name: "Sacred Stones Boutique", category: "Crystal Shop", location: "Sedona, AZ", featured: true, rating: 4.8, reviews: 89, services: ["Rare Crystals", "Custom Jewelry", "Readings"] },
  { id: 3, name: "Moonlight Mystics", category: "Psychic Services", location: "New Orleans, LA", featured: true, rating: 5.0, reviews: 203, services: ["Tarot", "Mediumship", "Past Life Regression"] },
  { id: 4, name: "Chakra Alignment Studio", category: "Wellness Center", location: "Austin, TX", featured: false, rating: 4.7, reviews: 56, services: ["Yoga", "Meditation", "Energy Work"] },
  { id: 5, name: "The Gemstone Emporium", category: "Crystal Shop", location: "Portland, OR", featured: false, rating: 4.6, reviews: 42, services: ["Wholesale", "Specimens", "Tumbled Stones"] },
  { id: 6, name: "Sage & Spirit Collective", category: "Spiritual Community", location: "Denver, CO", featured: false, rating: 4.8, reviews: 68, services: ["Workshops", "Retreats", "Ceremonies"] },
  { id: 7, name: "Third Eye Apothecary", category: "Holistic Shop", location: "San Francisco, CA", featured: false, rating: 4.5, reviews: 35, services: ["Herbs", "Crystals", "Essential Oils"] },
];

const tiers = [
  {
    id: 'regular',
    name: 'Regular Listing',
    price: 49,
    badge: null,
    color: '#9D4EDD',
    features: [
      'Standard directory placement',
      'Business profile with services',
      'Direct link to your website',
      'Customer reviews & ratings',
      'Cancel anytime',
    ],
  },
  {
    id: 'premium',
    name: 'Premium Listing',
    price: 150,
    badge: 'Most Popular',
    color: '#c9a96e',
    features: [
      'Featured placement (top of results)',
      'Business profile with services',
      'Direct link to your website',
      'Customer reviews & ratings',
      'Included in Metaphysical Hub',
      'Priority support',
      'Social media promotion',
      'Cancel anytime',
    ],
  },
];

const DirectoryPage = () => {
  usePageTitle('Metaphysical Business Directory');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTier, setSelectedTier] = useState('premium');
  const [form, setForm] = useState({
    businessName: '', ownerName: '', email: '', phone: '', website: '',
    category: '', location: '', services: '', description: ''
  });
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '', nameOnCard: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleCardChange = (e) => {
    let { name, value } = e.target;
    if (name === 'number') value = value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
    if (name === 'expiry') {
      value = value.replace(/\D/g, '').slice(0, 4);
      if (value.length > 2) value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (name === 'cvc') value = value.replace(/\D/g, '').slice(0, 4);
    setCard({ ...card, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!card.number || !card.expiry || !card.cvc || !card.nameOnCard) {
      toast.error('Please fill in all payment fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const rawNumber = card.number.replace(/\s/g, '');
      const encryptedCard = await encryptCardData({
        number: rawNumber, expiry: card.expiry, cvc: card.cvc, nameOnCard: card.nameOnCard,
      });

      const tier = tiers.find(t => t.id === selectedTier);
      const res = await fetch(`${API_URL}/api/directory-listing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          tier: selectedTier,
          monthlyRate: tier.price,
          encryptedCardData: encryptedCard,
        }),
      });
      if (res.ok) {
        toast.success(`Your ${tier.name} request has been submitted! We will review and publish within 48 hours.`);
        setFormSubmitted(true);
      } else throw new Error('Submit failed');
    } catch {
      toast.error('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filtered = searchQuery
    ? directoryListings.filter(l =>
        l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.services.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : directoryListings;

  const featured = filtered.filter(l => l.featured);
  const regular = filtered.filter(l => !l.featured);
  const activeTier = tiers.find(t => t.id === selectedTier);

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => document.getElementById('listing-form')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm";

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-[#1e1919] via-[#2a2030] to-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-10 right-20 w-80 h-80 bg-[#c9a96e]/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#9D4EDD]/30 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
            <Store className="w-4 h-4 text-[#c9a96e]" />
            <span className="text-sm text-white/80 tracking-widest uppercase">Metaphysical Directory</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Find Trusted <span className="text-[#c9a96e]">Healers & Shops</span>
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-8">
            The largest curated directory of crystal healers, metaphysical shops, spiritual
            practitioners, and wellness centers. Every listing is reviewed for authenticity.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={scrollToForm} className="px-8 py-3.5 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-xl hover:shadow-[#c9a96e]/30 transition-all" data-testid="list-your-business-btn">
              List Your Business — from $49/mo
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="bg-white border-b border-[#e8e4de]">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div><p className="font-serif text-2xl text-[#1e1919]">{directoryListings.length}+</p><p className="text-xs text-[#7A7A9A] uppercase tracking-wider">Active Listings</p></div>
            <div><p className="font-serif text-2xl text-[#1e1919]">12</p><p className="text-xs text-[#7A7A9A] uppercase tracking-wider">Categories</p></div>
            <div><p className="font-serif text-2xl text-[#1e1919]">50k+</p><p className="text-xs text-[#7A7A9A] uppercase tracking-wider">Monthly Visitors</p></div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A7A9A]" />
            <input type="text" placeholder="Search by name, category, location, or service..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="directory-search" />
          </div>
          <button className="flex items-center gap-2 px-6 py-3 bg-[#f8f5ff] rounded-xl text-[#61525a] hover:bg-[#9D4EDD]/10 transition-all text-sm font-semibold">
            <Filter className="w-4 h-4" /> Filter
          </button>
        </div>
      </div>

      {/* Featured */}
      {featured.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <div className="flex items-center gap-2 mb-6"><Crown className="w-5 h-5 text-[#c9a96e]" /><h2 className="font-serif text-2xl text-[#1e1919]">Featured Businesses</h2></div>
          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((listing) => (
              <div key={listing.id} className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#c9a96e]/30 hover:border-[#c9a96e] hover:shadow-xl transition-all" data-testid={`featured-listing-${listing.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] text-xs font-bold uppercase">Featured</span>
                  <div className="flex items-center gap-1"><Star className="w-4 h-4 text-[#FFB800] fill-current" /><span className="font-bold text-sm text-[#1e1919]">{listing.rating}</span><span className="text-xs text-[#7A7A9A]">({listing.reviews})</span></div>
                </div>
                <h3 className="font-serif text-xl text-[#1e1919] mb-1">{listing.name}</h3>
                <p className="text-sm text-[#9D4EDD] font-medium mb-2">{listing.category}</p>
                <div className="flex items-center gap-2 text-sm text-[#7A7A9A] mb-4"><MapPin className="w-4 h-4" /> {listing.location}</div>
                <div className="flex flex-wrap gap-1.5 mb-5">{listing.services.map((s, i) => (<span key={i} className="px-2.5 py-1 rounded-full bg-[#f8f5ff] text-[#61525a] text-xs">{s}</span>))}</div>
                <button className="w-full py-2.5 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-[#9D4EDD]/30 transition-all">View Profile</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Listings */}
      <div className="max-w-6xl mx-auto px-6 pb-12">
        <h2 className="font-serif text-xl text-[#1e1919] mb-6">All Listings</h2>
        <div className="space-y-3">
          {regular.map((listing) => (
            <div key={listing.id} className="bg-white rounded-xl p-5 border border-[#e8e4de] hover:shadow-md hover:border-[#9D4EDD]/20 transition-all flex items-center justify-between gap-4" data-testid={`listing-${listing.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9D4EDD]/10 to-[#FF6B9D]/10 flex items-center justify-center flex-shrink-0"><Store className="w-6 h-6 text-[#9D4EDD]" /></div>
                <div>
                  <h4 className="font-semibold text-[#1e1919]">{listing.name}</h4>
                  <p className="text-sm text-[#7A7A9A]">{listing.category} &bull; {listing.location}</p>
                  <div className="flex flex-wrap gap-1 mt-1">{listing.services.map((s, i) => (<span key={i} className="text-[10px] text-[#9D4EDD] bg-[#9D4EDD]/5 px-1.5 py-0.5 rounded">{s}</span>))}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="flex items-center gap-1"><Star className="w-4 h-4 text-[#FFB800] fill-current" /><span className="font-semibold text-sm">{listing.rating}</span></div>
                <ExternalLink className="w-5 h-5 text-[#c9a96e] cursor-pointer hover:text-[#9D4EDD] transition-colors" />
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#7A7A9A]"><Search className="w-8 h-8 mx-auto mb-3 opacity-40" /><p>No listings match your search. Try different keywords.</p></div>
        )}
      </div>

      {/* Why List + Pricing */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-[#f8f5ff]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl text-[#1e1919] mb-3">Why Advertise With Us?</h2>
            <p className="text-[#61525a] max-w-xl mx-auto">Reach thousands of spiritual seekers actively looking for trusted practitioners and crystal shops.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Users, title: '50,000+ Monthly Visitors', desc: 'Our community of crystal enthusiasts and spiritual seekers is growing every month.', color: '#9D4EDD' },
              { icon: Shield, title: 'Trusted & Curated', desc: 'Every listing is reviewed for authenticity. Your business joins an exclusive, quality directory.', color: '#c9a96e' },
              { icon: Sparkles, title: 'Targeted Audience', desc: 'Your ideal customers are already here — people seeking healing, crystals, and spiritual guidance.', color: '#FF6B9D' },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${item.color}15` }}><item.icon className="w-7 h-7" style={{ color: item.color }} /></div>
                <h3 className="font-semibold text-[#1e1919] mb-2">{item.title}</h3>
                <p className="text-sm text-[#61525a]">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Two-Tier Pricing */}
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl text-[#1e1919] mb-2">Choose Your Listing Plan</h2>
            <p className="text-[#61525a]">No hidden fees. Cancel anytime.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                onClick={() => { setSelectedTier(tier.id); scrollToForm(); }}
                className={`relative rounded-3xl p-8 text-center cursor-pointer transition-all hover:scale-[1.02] ${
                  tier.id === 'premium'
                    ? 'bg-gradient-to-br from-[#1e1919] to-[#2a2030] text-white shadow-2xl ring-2 ring-[#c9a96e]/50'
                    : 'bg-white border-2 border-[#e8e4de] hover:border-[#9D4EDD]/40'
                }`}
                data-testid={`tier-${tier.id}`}
              >
                {tier.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] text-xs font-bold uppercase">
                    {tier.badge}
                  </span>
                )}
                <h3 className={`font-serif text-2xl mb-2 ${tier.id === 'premium' ? 'text-[#c9a96e]' : 'text-[#1e1919]'}`}>{tier.name}</h3>
                <p className="mb-6">
                  <span className={`text-5xl font-serif ${tier.id === 'premium' ? 'text-white' : 'text-[#1e1919]'}`}>${tier.price}</span>
                  <span className={`text-lg ${tier.id === 'premium' ? 'text-white/50' : 'text-[#7A7A9A]'}`}>/mo</span>
                </p>
                <ul className="text-left space-y-3 mb-8">
                  {tier.features.map((f, i) => (
                    <li key={i} className={`flex items-center gap-2 text-sm ${tier.id === 'premium' ? 'text-white/80' : 'text-[#61525a]'}`}>
                      <CheckCircle className="w-4 h-4 text-[#00C853] flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  className={`w-full py-3 rounded-xl font-bold transition-all ${
                    tier.id === 'premium'
                      ? 'bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] hover:shadow-xl'
                      : 'bg-[#9D4EDD] text-white hover:bg-[#8B3DC9]'
                  }`}
                  data-testid={`select-tier-${tier.id}-btn`}
                >
                  Select {tier.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Listing Form */}
      {showForm && (
        <section className="py-16 px-6 bg-[#faf8f5]" id="listing-form" data-testid="listing-form-section">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-[#e8e4de]">
              {formSubmitted ? (
                <div className="text-center py-8" data-testid="listing-success">
                  <div className="w-16 h-16 rounded-full bg-[#00C853]/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#00C853]" />
                  </div>
                  <h3 className="font-serif text-2xl text-[#1e1919] mb-2">Listing Submitted!</h3>
                  <p className="text-[#61525a] max-w-md mx-auto">
                    Thank you for your interest. Our team will review your {activeTier.name} submission and reach out within 48 hours
                    to finalize your listing (${activeTier.price}/month).
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="directory-listing-form">
                  <div className="text-center mb-8">
                    <Store className="w-8 h-8 text-[#c9a96e] mx-auto mb-3" />
                    <h3 className="font-serif text-2xl text-[#1e1919] mb-1">List Your Business</h3>
                    {/* Tier Toggle */}
                    <div className="flex items-center justify-center gap-3 mt-4">
                      {tiers.map((tier) => (
                        <button
                          key={tier.id}
                          type="button"
                          onClick={() => setSelectedTier(tier.id)}
                          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                            selectedTier === tier.id
                              ? tier.id === 'premium'
                                ? 'bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] shadow-lg'
                                : 'bg-[#9D4EDD] text-white shadow-lg'
                              : 'bg-[#f8f5ff] text-[#61525a] hover:bg-[#9D4EDD]/10'
                          }`}
                          data-testid={`form-tier-${tier.id}`}
                        >
                          {tier.name} &mdash; ${tier.price}/mo
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Business Info */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input type="text" name="businessName" placeholder="Business Name *" value={form.businessName} onChange={handleChange} required className={inputCls} data-testid="listing-business-name" />
                      <input type="text" name="ownerName" placeholder="Owner / Contact Name *" value={form.ownerName} onChange={handleChange} required className={inputCls} data-testid="listing-owner-name" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input type="email" name="email" placeholder="Email Address *" value={form.email} onChange={handleChange} required className={inputCls} data-testid="listing-email" />
                      <input type="tel" name="phone" placeholder="Phone Number" value={form.phone} onChange={handleChange} className={inputCls} data-testid="listing-phone" />
                    </div>
                    <input type="url" name="website" placeholder="Website URL" value={form.website} onChange={handleChange} className={inputCls} data-testid="listing-website" />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <select name="category" value={form.category} onChange={handleChange} required className={`${inputCls} text-[#61525a]`} data-testid="listing-category">
                        <option value="">Select Category *</option>
                        <option value="Crystal Shop">Crystal Shop</option>
                        <option value="Healing Center">Healing Center</option>
                        <option value="Psychic Services">Psychic Services</option>
                        <option value="Wellness Center">Wellness Center</option>
                        <option value="Holistic Shop">Holistic Shop</option>
                        <option value="Spiritual Community">Spiritual Community</option>
                        <option value="Energy Worker">Energy Worker</option>
                        <option value="Other">Other</option>
                      </select>
                      <input type="text" name="location" placeholder="City, State *" value={form.location} onChange={handleChange} required className={inputCls} data-testid="listing-location" />
                    </div>
                    <input type="text" name="services" placeholder="Services offered (comma separated) *" value={form.services} onChange={handleChange} required className={inputCls} data-testid="listing-services" />
                    <textarea name="description" placeholder="Tell us about your business (optional)" value={form.description} onChange={handleChange} rows={3} className={`${inputCls} resize-none`} data-testid="listing-description" />

                    {/* Payment Section */}
                    <div className="border-t border-[#e8e4de] pt-6 mt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Lock className="w-4 h-4 text-[#00C853]" />
                        <h4 className="font-semibold text-[#1e1919] text-sm">Secure Payment &mdash; ${activeTier.price}/mo</h4>
                        <span className="ml-auto text-[10px] text-[#7A7A9A] uppercase tracking-wider flex items-center gap-1"><Shield className="w-3 h-3" /> RSA Encrypted</span>
                      </div>
                      <div className="space-y-3">
                        <input type="text" name="nameOnCard" placeholder="Name on Card *" value={card.nameOnCard} onChange={handleCardChange} required className={inputCls} data-testid="listing-card-name" />
                        <div className="relative">
                          <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A9A]" />
                          <input type="text" name="number" placeholder="Card Number *" value={card.number} onChange={handleCardChange} required className={`${inputCls} pl-11`} data-testid="listing-card-number" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input type="text" name="expiry" placeholder="MM/YY *" value={card.expiry} onChange={handleCardChange} required className={inputCls} data-testid="listing-card-expiry" />
                          <input type="text" name="cvc" placeholder="CVC *" value={card.cvc} onChange={handleCardChange} required className={inputCls} data-testid="listing-card-cvc" />
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3.5 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-lg hover:shadow-[#c9a96e]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="listing-submit-btn"
                    >
                      {isSubmitting ? (
                        <><div className="w-4 h-4 border-2 border-[#1e1919]/30 border-t-[#1e1919] rounded-full animate-spin" /> Processing...</>
                      ) : (
                        <><Send className="w-4 h-4" /> Submit {activeTier.name} &mdash; ${activeTier.price}/mo</>
                      )}
                    </button>
                    <p className="text-xs text-center text-[#7A7A9A]">
                      Your card is encrypted with RSA-2048 and securely transmitted. Cancel anytime.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default DirectoryPage;
